import React, { useEffect, useRef } from 'react';
import { PopupProps as KendoPopupProps, Popup as KendoPopup } from '@progress/kendo-react-popup';
import cn from 'clsx';
import { PopupService } from '@utils-clean/popup-service';

export interface PopupProps extends KendoPopupProps {
    'data-testid': string;
    border?: 'topBorder' | 'noBorder';
    alternativeStyle?: boolean;
    onBlur?: () => void;
    closeOnBlur?: boolean;
    popupCard?: boolean;
}

export const Popup = ({
    children,
    show,
    'data-testid': testId,
    border,
    popupClass,
    onBlur,
    alternativeStyle,
    closeOnBlur = true,
    popupCard,
    ...rest
}: PopupProps) => {
    const ref = useRef<KendoPopup>(null);

    useEffect(() => {
        const subscription = PopupService.closePopupsObservable().subscribe(() => onBlur?.());

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        // Kendo hides the element prop on the popup handler
        ref.current?.element?.setAttribute('data-testid', testId);
    }, [show]);

    const popupClasses = cn(popupClass, {
        noBorder: border === 'noBorder',
        topBorder: border === 'topBorder',
        secondary: alternativeStyle,
        popupCard,
    });

    let popup = (
        <KendoPopup ref={ref} show={show} popupClass={popupClasses} {...rest}>
            {children}
        </KendoPopup>
    );

    if (closeOnBlur) {
        popup = (
            <>
                {/* This Div allows click events outside the popup to close the popup.
                    Need to disable warnings for HTML element not being interactive,
                    it's not a typical interactive element that needs to account for keyboard events. Disabling just in this instance */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <div
                    style={{
                        width: '100vw',
                        height: '100vh',
                        display: show ? 'block' : 'none',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 2000,
                    }}
                    onClick={() => onBlur?.()}
                    tabIndex={-1}
                />
                {popup}
            </>
        );
    }

    return popup;
};
